import { useForm, emailPattern, TextFieldElement } from '@graphcommerce/ecommerce-ui'
import { useMutation } from '@graphcommerce/graphql'
import { MessageSnackbar, Button } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Typography, Box } from '@mui/material'
import { atob } from 'atob/node-atob'
import { StockSubscribeDocument } from '../AmxnotifStockSubscribe.gql'

export type FormGuestProps = React.ComponentProps<typeof FormGuest>

export function FormGuest(props: { productUid: string; wishlistButton?: React.ReactNode }) {
  const { productUid, wishlistButton } = props

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm()

  const [StockSubscribeMutation] = useMutation(StockSubscribeDocument)

  const onSubmit = async (data) => {
    await StockSubscribeMutation({
      variables: {
        product_id: parseInt(atob(productUid) as string, 10),
        email: data.emailAddress,
        gdpr_agreement: false,
      },
    })
  }

  const submitSuccess = !isSubmitting && isSubmitSuccessful

  return (
    <>
      <Typography component='p' variant='subtitle2' sx={[{ marginBottom: '5px' }]}>
        <Trans id='Subscribe to back in stock notification' />
      </Typography>
      <Box
        sx={[
          (theme) => ({
            display: 'grid',
            alignItems: 'center',
            gridTemplateAreas: `
              "input input"
              "button wishlist"
            `,
            gridTemplateColumns: '1fr auto',
            gridGap: theme.spacings.xxs,
            [theme.breakpoints.up('sm')]: {
              gridTemplateAreas: `
                "input button wishlist"
              `,
              gridTemplateColumns: '1fr auto auto',
            },
          }),
        ]}
      >
        <TextFieldElement
          variant='outlined'
          placeholder={i18n._(/* i18n */ 'Email address')}
          error={!!errors.emailAddress}
          control={control}
          name='emailAddress'
          validation={{
            required: i18n._(/* i18n */ 'This field is required'),
            pattern: { value: emailPattern, message: i18n._(/* i18n */ 'Invalid email address') },
          }}
          sx={(theme) => ({
            gridArea: 'input',
            '& .MuiInputBase-root': {
              height: '100%',
            },
            '& input': {
              boxSizing: 'border-box',
              paddingTop: theme.spacings.xxs,
              paddingBottom: theme.spacings.xxs,
              height: 'auto',
              lineHeight: 1,
            },
            [theme.breakpoints.up('sm')]: {
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                top: '100%',
                left: 0,
              },
            },
          })}
        />
        <Button
          type='submit'
          variant='contained'
          color='primary'
          disabled={submitSuccess}
          onClick={handleSubmit(onSubmit)}
          loading={isSubmitting}
          fullWidth
          sx={(theme) => ({
            gridArea: 'button',
            paddingTop: theme.spacings.xxs,
            paddingBottom: theme.spacings.xxs,
            height: 'auto',
            lineHeight: 1,
          })}
        >
          <Box component='span'>
            <Trans id='Sign up' />
          </Box>
        </Button>
        <Box sx={{ gridArea: 'wishlist' }}>{wishlistButton}</Box>
      </Box>
      <MessageSnackbar open={submitSuccess} variant='pill'>
        <Trans id='You are subscribed to the out of stock list' />
      </MessageSnackbar>
    </>
  )
}
